import React, {
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  Slide,
  Tooltip,
  Menu,
  MenuItem,
  ListItemText,
  alpha,
  Button,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  Tabs,
  Tab,
  Fab,
  Card,
  CardContent,
  Stack,
  Grid,
  InputAdornment,
} from '@mui/material';
import {
  Send as SendIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  History as HistoryIcon,
  Chat as ChatIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Store as StoreIcon,
  AutoAwesome as AutoAwesomeIcon,
  FileDownload as ExportIcon,
  Payment as PaymentIcon,
  PlayArrow as PlayIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

import RefundIcon from '@assets/icons/RefundIcon';
import TransactionsIcon from '@assets/icons/TransactionsIcon';
import OrdersIcon from '@assets/icons/OrdersIcon';
import SearchIcon from '@assets/icons/SearchIcon';
import { BananaContext } from '@wikimedia/react.i18n';
import APIKeyConfig from '../AIChat/APIKeyConfig';
import FormAutocompleteInput from '@legacy/components/formAutocompleteInput';
import AmountWithCurrency from '../../atoms/AmountWithCurrency';
import { decimalToIsoAmount, isoToDecimalAmount } from '../../../utils';
import { useAuth } from '@context/Auth0Context';
import { useUser } from '@hooks/useUser';
import { useSelector } from 'react-redux';
import QuickRequestService from '@services/ai/quickRequestService';
import api from '@legacy/api';

// Constants
const MAX_CHARS = 500;
const STORAGE_KEY = 'paybyrd_ai_smart_assistant_history';

// Smart suggestions factory function - needs translator context
const createSmartSuggestions = (translator) => [
  {
    id: 'create-payment-link',
    title: translator.i18n('ai_smart_suggestion_create_payment_link'),
    description: translator.i18n('ai_smart_suggestion_create_payment_link_description'),
    icon: PaymentIcon,
    color: 'primary',
    template: translator.i18n('ai_smart_suggestion_create_payment_link_template'),
    variables: [
      {
        key: 'orderRef',
        label: translator.i18n('ai_smart_suggestion_order_reference'),
        type: 'text',
        placeholder: translator.i18n('ai_smart_suggestion_order_reference_placeholder'),
        required: true,
      },
      {
        key: 'amount',
        label: translator.i18n('ai_smart_suggestion_amount'),
        type: 'number',
        placeholder: translator.i18n('ai_smart_suggestion_amount_placeholder'),
        required: true,
      },
      {
        key: 'currency',
        label: translator.i18n('ai_smart_suggestion_currency'),
        type: 'select',
        options: ['EUR', 'USD', 'GBP'],
        placeholder: translator.i18n('ai_smart_suggestion_currency_placeholder'),
        required: true,
      },
      {
        key: 'email',
        label: translator.i18n('ai_smart_suggestion_customer_email'),
        type: 'email',
        placeholder: translator.i18n('ai_smart_suggestion_customer_email_placeholder'),
        required: true,
      },
    ],
  },
  {
    id: 'find-order',
    title: translator.i18n('ai_smart_suggestion_find_order'),
    description: translator.i18n('ai_smart_suggestion_find_order_description'),
    icon: OrdersIcon,
    color: 'primary',
    template: translator.i18n('ai_smart_suggestion_find_order_template'),
    variables: [
      {
        key: 'orderId',
        label: translator.i18n('ai_smart_suggestion_order_id'),
        type: 'text',
        placeholder: translator.i18n('ai_smart_suggestion_order_id_placeholder'),
        required: true,
      },
    ],
  },
  {
    id: 'search-orders-by-ref',
    title: translator.i18n('ai_smart_suggestion_search_orders_by_ref'),
    description: translator.i18n('ai_smart_suggestion_search_orders_by_ref_description'),
    icon: SearchIcon,
    color: 'primary',
    template: translator.i18n('ai_smart_suggestion_search_orders_by_ref_template'),
    variables: [
      {
        key: 'orderRef',
        label: translator.i18n('ai_smart_suggestion_order_reference'),
        type: 'text',
        placeholder: translator.i18n('ai_smart_suggestion_order_reference_placeholder'),
        required: true,
      },
    ],
  },
  {
    id: 'retrieve-transaction',
    title: translator.i18n('ai_smart_suggestion_retrieve_transaction'),
    description: translator.i18n('ai_smart_suggestion_retrieve_transaction_description'),
    icon: TransactionsIcon,
    color: 'primary',
    template: translator.i18n('ai_smart_suggestion_retrieve_transaction_template'),
    variables: [
      {
        key: 'transactionId',
        label: translator.i18n('ai_smart_suggestion_transaction_id'),
        type: 'text',
        placeholder: translator.i18n('ai_smart_suggestion_transaction_id_placeholder'),
        required: true,
      },
    ],
  },
  {
    id: 'refund-transaction',
    title: translator.i18n('ai_smart_suggestion_refund_transaction'),
    description: translator.i18n('ai_smart_suggestion_refund_transaction_description'),
    icon: RefundIcon,
    color: 'primary',
    template: translator.i18n('ai_smart_suggestion_refund_transaction_template'),
    variables: [
      {
        key: 'transactionId',
        label: translator.i18n('ai_smart_suggestion_transaction_id'),
        type: 'text',
        placeholder: translator.i18n('ai_smart_suggestion_transaction_id_placeholder'),
        required: true,
      },
      {
        key: 'amount',
        label: translator.i18n('ai_smart_suggestion_amount'),
        type: 'number',
        placeholder: translator.i18n('ai_smart_suggestion_amount_placeholder'),
        required: true,
      },
      {
        key: 'currency',
        label: translator.i18n('ai_smart_suggestion_currency'),
        type: 'select',
        options: ['EUR', 'USD', 'GBP'],
        placeholder: translator.i18n('ai_smart_suggestion_currency_placeholder'),
        required: true,
      },
    ],
  },
  {
    id: 'export-orders',
    title: translator.i18n('ai_smart_suggestion_export_orders'),
    description: translator.i18n('ai_smart_suggestion_export_orders_description'),
    icon: ExportIcon,
    color: '#2196f3',
    template: translator.i18n('ai_smart_suggestion_export_orders_template_with_format'),
    variables: [
      {
        key: 'format',
        label: translator.i18n('ai_smart_suggestion_export_format'),
        type: 'select',
        options: ['XLSX', 'CSV'],
        placeholder: translator.i18n('ai_smart_suggestion_export_format_placeholder'),
        required: true,
      },
    ],
  },
];

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Custom hooks for state management
const useAISmartAssistantState = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  // Request states
  const [currentResponse, setCurrentResponse] = useState('');
  const [hasCurrentResponse, setHasCurrentResponse] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [isRequestCompleted, setIsRequestCompleted] = useState(false);
  
  // History and suggestions
  const [requestHistory, setRequestHistory] = useState([]);
  const [selectedHistoryItem, setSelectedHistoryItem] = useState(null);
  const [suggestionVariables, setSuggestionVariables] = useState({});
  const [expandedSuggestion, setExpandedSuggestion] = useState(null);
  
  // UI states
  const [hasApiKey, setHasApiKey] = useState(false);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  
  return {
    // Modal state
    isOpen, setIsOpen,
    currentTab, setCurrentTab,
    
    // Input state
    input, setInput,
    charCount, setCharCount,
    isLoading, setIsLoading,
    
    // Request state
    currentResponse, setCurrentResponse,
    hasCurrentResponse, setHasCurrentResponse,
    pendingConfirmation, setPendingConfirmation,
    conversationHistory, setConversationHistory,
    isRequestCompleted, setIsRequestCompleted,
    
    // History and suggestions
    requestHistory, setRequestHistory,
    selectedHistoryItem, setSelectedHistoryItem,
    suggestionVariables, setSuggestionVariables,
    expandedSuggestion, setExpandedSuggestion,
    
    // UI state
    hasApiKey, setHasApiKey,
    apiKeyDialogOpen, setApiKeyDialogOpen,
    errorMessage, setErrorMessage,
  };
};

// Helper functions
const getInputPlaceholder = (pendingConfirmation, isRequestCompleted, translator) => {
  if (pendingConfirmation) {
    return translator.i18n('ai_smart_assistant_placeholder_confirm');
  }
  if (isRequestCompleted) {
    return translator.i18n('ai_smart_assistant_placeholder_completed');
  }
  return translator.i18n('ai_smart_assistant_placeholder_default');
};

const isInputDisabled = (isLoading, selectedHistoryItem, pendingConfirmation, isRequestCompleted) => {
  return isLoading || !!selectedHistoryItem || !!pendingConfirmation || isRequestCompleted;
};

const isSendDisabled = (input, isLoading, selectedHistoryItem, pendingConfirmation, isRequestCompleted) => {
  return input.trim() === '' || isLoading || !!selectedHistoryItem || !!pendingConfirmation || isRequestCompleted;
};

const shouldShowNewRequestButton = (selectedHistoryItem, isRequestCompleted) => {
  return selectedHistoryItem || isRequestCompleted;
};

// Main component
export default function AISmartAssistant({ isAIEnabled = false }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const quickRequestService = useRef(new QuickRequestService()).current;
  const { accessToken } = useAuth();
  const plMerchants = useSelector((state) => state.user.plMerchants);
  const { merchants } = useUser();
  const [selectedStore, setSelectedStore] = useState(null);
  const translator = useContext(BananaContext);
  
  const state = useAISmartAssistantState();
  
  // Create smart suggestions with translations
  const smartSuggestions = useMemo(() => createSmartSuggestions(translator), [translator]);
  
  // Load history and initialize
  useEffect(() => {
    const savedHistory = localStorage.getItem(STORAGE_KEY);
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        state.setRequestHistory(parsedHistory.slice(0, 20));
      } catch (error) {
        console.error('Error parsing request history:', error);
      }
    }
    state.setHasApiKey(quickRequestService.hasApiKey());
  }, []);

  // Save history to localStorage
  useEffect(() => {
    if (state.requestHistory.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state.requestHistory));
    }
  }, [state.requestHistory]);

  // Auto-select first AI-enabled store
  useEffect(() => {
    if (!selectedStore && merchants && plMerchants) {
      const merchantIdsWithAI = merchants
        .filter((merchant) => merchant?.accountConfigurations?.isAIAssistantEnabled === true)
        .map((merchant) => merchant.personId);

      const aiEnabledMerchants = plMerchants.filter((merchant) =>
        merchantIdsWithAI.includes(Number(merchant.value))
      );

      if (aiEnabledMerchants.length > 0) {
        setSelectedStore(aiEnabledMerchants[0]);
      }
    }
  }, [merchants, plMerchants, selectedStore]);

  // Global event listener
  useEffect(() => {
    const handleOpenAISmartAssistant = () => {
      if (isAIEnabled) {
        state.setIsOpen(true);
      }
    };

    window.addEventListener('openAISmartAssistant', handleOpenAISmartAssistant);
    return () => {
      window.removeEventListener('openAISmartAssistant', handleOpenAISmartAssistant);
    };
  }, [isAIEnabled]);

  // Event handlers
  const handleInputChange = useCallback((e) => {
    const inputText = e.target.value;
    if (inputText.length <= MAX_CHARS) {
      state.setInput(inputText);
      state.setCharCount(inputText.length);
    }
  }, []);

  const addToHistory = useCallback((question, response, provider) => {
    const newRequest = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      question,
      response,
      provider,
      store: selectedStore?.label || 'No store selected',
    };

    state.setRequestHistory(prev => [newRequest, ...prev.slice(0, 19)]);
  }, [selectedStore]);

  const handleClear = useCallback(() => {
    state.setInput('');
    state.setCharCount(0);
    state.setCurrentResponse('');
    state.setHasCurrentResponse(false);
    state.setErrorMessage('');
    state.setSelectedHistoryItem(null);
    state.setPendingConfirmation(null);
    state.setConversationHistory([]);
    state.setIsRequestCompleted(false);
  }, []);

  const handleConfirmationResponse = useCallback(async (confirmed) => {
    if (!state.pendingConfirmation) return;

    state.setCurrentResponse('');
    state.setHasCurrentResponse(false);
    state.setIsLoading(true);
    state.setPendingConfirmation(null);

    try {
      if (confirmed) {
        const confirmationMessage = 'Yes, I confirm. Please proceed with the refund.';
        
        const updatedHistory = [
          ...state.conversationHistory,
          { text: confirmationMessage, isUser: true }
        ];
        
        const aiResponse = await quickRequestService.sendMessageWithHistory(
          confirmationMessage,
          updatedHistory,
          accessToken,
          selectedStore
        );

        state.setCurrentResponse(aiResponse);
        state.setHasCurrentResponse(true);
        
        addToHistory(state.pendingConfirmation.originalQuestion, aiResponse, quickRequestService.getProvider());
      } else {
        const cancelResponse = translator.i18n('ai_smart_assistant_request_canceled');
        state.setCurrentResponse(cancelResponse);
        state.setHasCurrentResponse(true);
        
        addToHistory(state.pendingConfirmation.originalQuestion, cancelResponse, quickRequestService.getProvider());
      }
      
      state.setConversationHistory([]);
      state.setIsRequestCompleted(true);
      
      if (isMobile) {
        state.setCurrentTab(0);
      }

    } catch (error) {
      console.error('Error handling confirmation:', error);
      state.setErrorMessage(translator.i18n('ai_smart_assistant_error_confirmation'));
      state.setHasCurrentResponse(true);
      state.setConversationHistory([]);
      state.setIsRequestCompleted(true);
    } finally {
      state.setIsLoading(false);
    }
  }, [state.pendingConfirmation, state.conversationHistory, quickRequestService, accessToken, selectedStore, addToHistory, isMobile]);

  const handleSend = useCallback(async (customMessage = null) => {
    const messageToSend = customMessage || state.input;
    if (messageToSend.trim() === '') return;

    if (!state.hasApiKey) {
      state.setErrorMessage(translator.i18n('ai_smart_assistant_error_api_key'));
      state.setApiKeyDialogOpen(true);
      return;
    }

    state.setIsLoading(true);
    state.setHasCurrentResponse(false);
    state.setCurrentResponse('');
    state.setErrorMessage('');
    state.setSelectedHistoryItem(null);
    state.setPendingConfirmation(null);
    state.setIsRequestCompleted(false);

    if (customMessage) {
      state.setInput(customMessage);
      state.setCharCount(customMessage.length);
    }

    try {
      const currentHistory = state.conversationHistory.length > 0 ? state.conversationHistory : [];
      
      const updatedHistory = [
        ...currentHistory,
        { text: messageToSend, isUser: true }
      ];
      
      const aiResponse = await quickRequestService.sendMessageWithHistory(
        messageToSend,
        currentHistory,
        accessToken,
        selectedStore
      );

      if (aiResponse.toUpperCase().includes('REQUIRES_CONFIRMATION')) {
        state.setPendingConfirmation({
          originalQuestion: messageToSend,
          response: aiResponse
        });
        
        state.setConversationHistory([
          ...updatedHistory,
          { text: aiResponse, isUser: false }
        ]);
      } else {
        state.setConversationHistory([
          ...updatedHistory,
          { text: aiResponse, isUser: false }
        ]);
        
        addToHistory(messageToSend, aiResponse, quickRequestService.getProvider());
      }

      state.setCurrentResponse(aiResponse);
      state.setHasCurrentResponse(true);
      state.setIsRequestCompleted(true);
      
      if (isMobile) {
        state.setCurrentTab(0);
      }

    } catch (error) {
      console.error('Error sending request:', error);

      if (error.message.includes('API key')) {
        state.setErrorMessage(translator.i18n('ai_smart_assistant_error_invalid_api_key'));
        state.setApiKeyDialogOpen(true);
      } else if (error.message.includes('Auth token')) {
        state.setErrorMessage(translator.i18n('ai_smart_assistant_error_auth'));
      } else {
        state.setErrorMessage(translator.i18n('ai_smart_assistant_error_request'));
      }
    } finally {
      state.setIsLoading(false);
    }
  }, [state.input, state.hasApiKey, accessToken, selectedStore, quickRequestService, addToHistory, isMobile, state.conversationHistory]);

  const handleSuggestionVariableChange = useCallback((suggestionId, variableKey, value) => {
    state.setSuggestionVariables(prev => ({
      ...prev,
      [suggestionId]: {
        ...prev[suggestionId],
        [variableKey]: value,
      },
    }));
  }, []);

  const handleSuggestionExecute = useCallback((suggestion) => {
    let finalPrompt = suggestion.template;
    
    if (suggestion.variables && suggestion.variables.length > 0) {
      const variables = state.suggestionVariables[suggestion.id] || {};
      
      const missingRequired = suggestion.variables.filter(
        variable => variable.required && !variables[variable.key]
      );
      
      if (missingRequired.length > 0) {
        state.setErrorMessage(`${translator.i18n('ai_smart_suggestion_missing_fields')} ${missingRequired.map(v => v.label).join(', ')}`);
        return;
      }
      
      suggestion.variables.forEach(variable => {
        const value = variables[variable.key] || '';
        finalPrompt = finalPrompt.replace(`{${variable.key}}`, value);
      });
    }
    
    handleClear();
    handleSend(finalPrompt);
    state.setCurrentTab(0);
    state.setExpandedSuggestion(null);
  }, [state.suggestionVariables, handleSend, handleClear]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleNewRequest = useCallback(() => {
    handleClear();
    state.setCurrentTab(0);
  }, [handleClear]);

  const handleClose = useCallback(() => {
    state.setIsOpen(false);
    setTimeout(() => {
      handleClear();
      state.setCurrentTab(0);
      state.setExpandedSuggestion(null);
      state.setSuggestionVariables({});
    }, 300);
  }, [handleClear]);

  const handleHistoryItemClick = useCallback((item) => {
    state.setSelectedHistoryItem(item);
    state.setInput(item.question);
    state.setCharCount(item.question.length);
    state.setCurrentResponse(item.response);
    state.setHasCurrentResponse(true);
    state.setIsRequestCompleted(false);
    
    if (isMobile) {
      state.setCurrentTab(0);
    }
  }, [isMobile]);

  const handleDeleteHistoryItem = useCallback((itemId, event) => {
    event.stopPropagation();
    state.setRequestHistory(prev => prev.filter(item => item.id !== itemId));
  }, []);

  const handleClearHistory = useCallback(() => {
    state.setRequestHistory([]);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  // API key handlers
  const handleOpenApiKeyDialog = () => state.setApiKeyDialogOpen(true);
  const handleCloseApiKeyDialog = () => state.setApiKeyDialogOpen(false);
  const handleApiKeySaved = () => {
    state.setHasApiKey(quickRequestService.hasApiKey());
    state.setErrorMessage('');
  };

  const handleTabChange = (_, newValue) => {
    state.setCurrentTab(newValue);
  };

  if (!isAIEnabled) {
    return null;
  }

  // Render components
  const renderStoreSelection = () => (
    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <FormAutocompleteInput
        options={plMerchants?.filter(merchant => {
          const merchantData = merchants?.find(m => m.personId === Number(merchant.value));
          return merchantData?.accountConfigurations?.isAIAssistantEnabled === true;
        }) || []}
        value={selectedStore}
        placeholder={translator.i18n('ai_smart_assistant_search_stores')}
        onChange={(event, newValue) => {
          event?.preventDefault?.();
          event?.stopPropagation?.();
          setSelectedStore(newValue);
        }}
        label={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <StoreIcon sx={{ mr: 1, fontSize: '1rem' }} />
            <Typography variant="caption">{translator.i18n('ai_smart_assistant_store_selection')}</Typography>
          </Box>
        }
        style={{ width: '100%' }}
        disabled={false}
      />
    </Box>
  );

  const renderInputSection = () => (
    <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        {state.selectedHistoryItem ? translator.i18n('ai_smart_assistant_selected_request') : translator.i18n('ai_smart_assistant_your_question')}
      </Typography>
      
      {state.errorMessage && (
        <Alert
          severity="warning"
          size="small"
          sx={{ mb: 2 }}
          onClose={() => state.setErrorMessage('')}
          action={
            state.errorMessage.includes('API key') ? (
              <Button
                color="inherit"
                size="small"
                onClick={handleOpenApiKeyDialog}
              >
                {translator.i18n('ai_smart_assistant_configure_api_key')}
              </Button>
            ) : null
          }
        >
          {state.errorMessage}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={isMobile ? 3 : 4}
        placeholder={getInputPlaceholder(state.pendingConfirmation, state.isRequestCompleted, translator)}
        value={state.input}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        variant="outlined"
        disabled={isInputDisabled(state.isLoading, state.selectedHistoryItem, state.pendingConfirmation, state.isRequestCompleted)}
        autoFocus={!isMobile && !state.selectedHistoryItem && !state.pendingConfirmation && !state.isRequestCompleted}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
        <Typography
          variant="caption"
          color={state.charCount > MAX_CHARS * 0.8 ? 'warning.main' : 'text.secondary'}
        >
          {state.charCount}/{MAX_CHARS} {translator.i18n('ai_smart_assistant_characters')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          {shouldShowNewRequestButton(state.selectedHistoryItem, state.isRequestCompleted) && (
            <Button
              variant="outlined"
              onClick={handleNewRequest}
              size="small"
            >
              {translator.i18n('ai_smart_assistant_new_request')}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={() => handleSend()}
            disabled={isSendDisabled(state.input, state.isLoading, state.selectedHistoryItem, state.pendingConfirmation, state.isRequestCompleted)}
            startIcon={state.isLoading ? <CircularProgress size={16} /> : <SendIcon />}
          >
            {state.isLoading ? translator.i18n('ai_smart_assistant_processing_button') : translator.i18n('ai_smart_assistant_send')}
          </Button>
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 1, fontSize: '0.75rem' }}
      >
        {translator.i18n('ai_smart_assistant_tip')}
      </Typography>
    </Box>
  );

  const renderResponseSection = () => (
    <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {translator.i18n('ai_smart_assistant_ai_response')}
          {(state.hasCurrentResponse || state.selectedHistoryItem) && (
            <Chip
              size="small"
              label={state.selectedHistoryItem ? state.selectedHistoryItem.provider.toUpperCase() : quickRequestService.getProvider() === 'openai' ? 'ChatGPT' : 'Claude'}
              sx={{ ml: 1, height: 18, fontSize: '0.6rem' }}
            />
          )}
        </Typography>
        
        {/* Confirmation Alert - Always visible at top */}
        {state.pendingConfirmation && !state.isLoading && !state.selectedHistoryItem && (
          <Alert
            severity="warning"
            sx={{ 
              mt: 2,
              '& .MuiAlert-message': { 
                width: '100%' 
              }
            }}
            action={
              <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<CancelIcon />}
                  onClick={() => handleConfirmationResponse(false)}
                  disabled={state.isLoading}
                >
                  {translator.i18n('ai_smart_assistant_cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  startIcon={<CheckCircleIcon />}
                  onClick={() => handleConfirmationResponse(true)}
                  disabled={state.isLoading}
                >
                  {translator.i18n('ai_smart_assistant_confirm')}
                </Button>
              </Box>
            }
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {translator.i18n('ai_smart_assistant_confirmation_required')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translator.i18n('ai_smart_assistant_confirmation_description')}
            </Typography>
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          flexGrow: 1,
          p: 2,
          overflow: 'auto',
          backgroundColor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        {!state.isLoading && !state.hasCurrentResponse && !state.selectedHistoryItem && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {translator.i18n('ai_smart_assistant_ready_help')}
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 300 }}>
              {translator.i18n('ai_smart_assistant_ready_help_description')}
            </Typography>
          </Box>
        )}

        {state.isLoading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 2,
            }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              {translator.i18n('ai_smart_assistant_processing')}
            </Typography>
          </Box>
        )}

        {(state.hasCurrentResponse || state.selectedHistoryItem) && (
          <Box
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                fontFamily: 'inherit',
                fontWeight: 600,
                marginTop: theme.spacing(2),
                marginBottom: theme.spacing(1),
                '&:first-of-type': {
                  marginTop: 0,
                },
              },
              '& h1': { fontSize: '1.5rem' },
              '& h2': { fontSize: '1.3rem' },
              '& h3': { fontSize: '1.1rem' },
              '& h4, & h5, & h6': { fontSize: '1rem' },
              '& p': {
                marginTop: 0,
                marginBottom: theme.spacing(1),
                lineHeight: 1.6,
                fontSize: '0.95rem',
                '&:last-child': {
                  marginBottom: 0,
                },
              },
              '& ul, & ol': {
                marginTop: theme.spacing(0.5),
                marginBottom: theme.spacing(1),
                paddingLeft: theme.spacing(3),
              },
              '& li': {
                marginBottom: theme.spacing(0.5),
                lineHeight: 1.5,
              },
              '& code': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                fontFamily: 'monospace',
              },
              '& pre': {
                backgroundColor: alpha(theme.palette.grey[500], 0.1),
                padding: theme.spacing(2),
                borderRadius: '8px',
                overflow: 'auto',
                margin: `${theme.spacing(1)} 0`,
                '& code': {
                  backgroundColor: 'transparent',
                  padding: 0,
                },
              },
              '& blockquote': {
                borderLeft: `4px solid ${theme.palette.primary.main}`,
                paddingLeft: theme.spacing(2),
                margin: `${theme.spacing(1)} 0`,
                fontStyle: 'italic',
                color: theme.palette.text.secondary,
              },
              '& table': {
                width: '100%',
                borderCollapse: 'collapse',
                margin: `${theme.spacing(1)} 0`,
              },
              '& th, & td': {
                border: `1px solid ${theme.palette.divider}`,
                padding: theme.spacing(1),
                textAlign: 'left',
              },
              '& th': {
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                fontWeight: 600,
              },
              '& strong': {
                fontWeight: 600,
              },
              '& em': {
                fontStyle: 'italic',
              },
              '& a': {
                color: theme.palette.primary.main,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '& hr': {
                border: 'none',
                borderTop: `1px solid ${theme.palette.divider}`,
                margin: `${theme.spacing(2)} 0`,
              },
            }}
          >
            <ReactMarkdown>
              {state.selectedHistoryItem ? state.selectedHistoryItem.response : state.currentResponse}
            </ReactMarkdown>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderChatContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {renderStoreSelection()}
      {renderInputSection()}
      {renderResponseSection()}
    </Box>
  );

  const renderSuggestionsContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {translator.i18n('ai_smart_assistant_smart_suggestions')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {translator.i18n('ai_smart_assistant_smart_suggestions_description')}
        </Typography>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
        <Stack spacing={2}>
          {smartSuggestions.map((suggestion) => {
            const IconComponent = suggestion.icon;
            const isExpanded = state.expandedSuggestion === suggestion.id;
            const variables = state.suggestionVariables[suggestion.id] || {};
            
            return (
              <Card
                key={suggestion.id}
                elevation={isExpanded ? 3 : 0}
                sx={{
                  transition: 'all 0.2s ease-in-out',
                  border: '1px solid',
                  borderColor: isExpanded ? theme.palette.primary.main : 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    borderColor: theme.palette.purple.main,
                    backgroundColor: alpha(theme.palette.purple.main, 0.05),
                    transform: isExpanded ? 'none' : 'translateY(-1px)',
                    boxShadow: isExpanded ? 3 : 1,
                  },
                }}
                onClick={() => {
                  if (suggestion.variables.length > 0 && !isExpanded) {
                    state.setExpandedSuggestion(suggestion.id);
                  } else if (suggestion.variables.length === 0) {
                    handleSuggestionExecute(suggestion);
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                      <IconComponent 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontSize: '1.5rem'
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {suggestion.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {suggestion.description}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {suggestion.variables.length === 0 && (
                      <PlayIcon 
                        sx={{ 
                          color: theme.palette.primary.main,
                          fontSize: '1.2rem'
                        }}
                      />
                    )}
                  </Box>

                  {isExpanded && suggestion.variables.length > 0 && (
                    <Box 
                      sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary' }}>
                        Template: {suggestion.template}
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {suggestion.variables.map((variable) => {
                          if (variable.key === 'amount' && suggestion.variables.some(v => v.key === 'currency')) {
                            const currencyVar = suggestion.variables.find(v => v.key === 'currency');
                            if (variable.key === 'currency') return null;
                            
                            return (
                              <Grid item xs={12} key={variable.key}>
                                <AmountWithCurrency
                                  value={{
                                    amount: variables[variable.key] ? decimalToIsoAmount(variables[variable.key], variables[currencyVar.key] || 'EUR') : undefined,
                                    currency: variables[currencyVar.key] || 'EUR'
                                  }}
                                  onChange={(newValue) => {
                                    if (newValue.amount) {
                                      const decimalAmount = isoToDecimalAmount(newValue.amount, newValue.currency);
                                      handleSuggestionVariableChange(suggestion.id, variable.key, decimalAmount);
                                    } else {
                                      handleSuggestionVariableChange(suggestion.id, variable.key, '');
                                    }
                                    handleSuggestionVariableChange(suggestion.id, currencyVar.key, newValue.currency);
                                  }}
                                  getCurrencies={() => api.getCurrenciesKeyValue()}
                                  defaultCurrency="EUR"
                                />
                              </Grid>
                            );
                          } else if (variable.key === 'currency' && suggestion.variables.some(v => v.key === 'amount')) {
                            return null;
                          } else if (variable.type === 'select') {
                            return (
                              <Grid item xs={12} key={variable.key}>
                                <TextField
                                  select
                                  fullWidth
                                  size="small"
                                  value={variables[variable.key] || ''}
                                  onChange={(e) => handleSuggestionVariableChange(suggestion.id, variable.key, e.target.value)}
                                  required={variable.required}
                                  placeholder={variable.placeholder}
                                >
                                  {variable.options.map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </Grid>
                            );
                          } else {
                            return (
                              <Grid item xs={12} key={variable.key}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  type={variable.type}
                                  value={variables[variable.key] || ''}
                                  onChange={(e) => handleSuggestionVariableChange(suggestion.id, variable.key, e.target.value)}
                                  required={variable.required}
                                  placeholder={variable.placeholder}
                                  InputProps={{
                                    startAdornment: variable.type === 'email' ? (
                                      <InputAdornment position="start">
                                        <EmailIcon fontSize="small" />
                                      </InputAdornment>
                                    ) : null,
                                  }}
                                />
                              </Grid>
                            );
                          }
                        })}
                      </Grid>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => state.setExpandedSuggestion(null)}
                        >
                          {translator.i18n('ai_smart_assistant_cancel')}
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleSuggestionExecute(suggestion)}
                          startIcon={<PlayIcon />}
                          sx={{ 
                            bgcolor: theme.palette.primary.main,
                            '&:hover': {
                              bgcolor: theme.palette.primary.main,
                              filter: 'brightness(0.9)',
                            },
                          }}
                        >
                          {translator.i18n('ai_smart_suggestion_execute')}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );

  const renderHistoryContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {translator.i18n('ai_smart_assistant_request_history')} ({state.requestHistory.length})
          </Typography>
          {state.requestHistory.length > 0 && (
            <Button
              size="small"
              onClick={handleClearHistory}
              startIcon={<DeleteIcon />}
              color="error"
            >
              {translator.i18n('ai_smart_assistant_clear_all')}
            </Button>
          )}
        </Box>
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {state.requestHistory.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary',
              p: 3,
            }}
          >
            <HistoryIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {translator.i18n('ai_smart_assistant_no_history')}
            </Typography>
            <Typography variant="body2">
              {translator.i18n('ai_smart_assistant_no_history_description')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {state.requestHistory.map((item) => (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  onClick={() => handleHistoryItemClick(item)}
                  sx={{
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    p: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.timestamp).toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        size="small"
                        label={item.provider === 'openai' ? 'ChatGPT' : 'Claude'}
                        sx={{ height: 16, fontSize: '0.6rem' }}
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteHistoryItem(item.id, e)}
                        sx={{ p: 0.5 }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      mb: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {item.question}
                  </Typography>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={state.isOpen}
        onClose={handleClose}
        fullScreen
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            backgroundColor: 'background.default',
            backgroundImage: 'none',
            margin: 0,
            padding: 0,
            maxWidth: 'none',
            maxHeight: 'none',
            borderRadius: 0,
          },
        }}
      >
        <AppBar 
          position="sticky" 
          elevation={1}
          sx={{ 
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Toolbar sx={{ px: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              <AutoAwesomeIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {translator.i18n('ai_smart_assistant_title')}
              </Typography>
              <Chip
                size="small"
                label="SMART"
                color="secondary"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6rem' }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title={translator.i18n('ai_smart_assistant_help_tooltip')} placement="bottom" arrow>
                <IconButton 
                  component="a" 
                  href="https://docs.paybyrd.com/docs/ai-smart-assistant" 
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  sx={{ 
                    color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={translator.i18n('ai_smart_assistant_configure_api_key')} placement="bottom" arrow>
                <IconButton 
                  onClick={handleOpenApiKeyDialog}
                  size="small"
                  sx={{ 
                    color: theme => theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'transparent'
                    }
                  }}
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <IconButton 
                edge="end" 
                onClick={handleClose}
                size="small"
                sx={{ 
                  color: theme => theme.palette.mode === 'dark' ? 'text.secondary' : 'text.primary',
                  '&:hover': {
                    color: 'text.primary',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
          }}
        >
          {isMobile ? (
            <>
              <Tabs
                value={state.currentTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <Tab 
                  icon={<ChatIcon />} 
                  label={translator.i18n('ai_smart_assistant_chat_tab')} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<AutoAwesomeIcon />} 
                  label={translator.i18n('ai_smart_assistant_smart_tab')} 
                  iconPosition="start"
                />
                <Tab 
                  icon={<HistoryIcon />} 
                  label={`${translator.i18n('ai_smart_assistant_history_tab')} (${state.requestHistory.length})`} 
                  iconPosition="start"
                />
              </Tabs>
              
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                {state.currentTab === 0 && renderChatContent()}
                {state.currentTab === 1 && renderSuggestionsContent()}
                {state.currentTab === 2 && renderHistoryContent()}
              </Box>

              {state.currentTab !== 0 && (
                <Fab
                  color="primary"
                  onClick={handleNewRequest}
                  sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    zIndex: 1,
                  }}
                >
                  <AddIcon />
                </Fab>
              )}
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexGrow: 1,
                overflow: 'hidden',
              }}
            >
              <Paper
                elevation={0}
                sx={{
                  width: '25%',
                  height: '100%',
                  borderRadius: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: alpha(theme.palette.secondary.main, 0.02),
                }}
              >
                {renderSuggestionsContent()}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  width: '50%',
                  height: '100%',
                  borderRadius: 0,
                  borderRight: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                }}
              >
                {renderChatContent()}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  width: '25%',
                  height: '100%',
                  borderRadius: 0,
                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                {renderHistoryContent()}
              </Paper>
            </Box>
          )}
        </Box>
      </Dialog>

      <APIKeyConfig
        open={state.apiKeyDialogOpen}
        onClose={handleCloseApiKeyDialog}
        onSave={handleApiKeySaved}
        chatService={quickRequestService}
      />
    </>
  );
}